/**
 * Store-facing reading of a sync run's skip reasons and product failures.
 *
 * The run stores a skip reason as a Firestore map key. Keys that named a
 * product were cut at 140 characters, so the instruction at the end ("Confirm
 * the listing…") never reached the store, and each product became its own row.
 * New runs bucket those into the sentences in `SYNC_SKIP_BUCKETS`. This reader
 * understands both, and it says which rows count toward "Blocked in sync".
 *
 * A button exists only when the store can act from this row: a Sync Behavior
 * setting, or (Shopify) the listings waiting to be confirmed. A rate-limit
 * failure is not a product issue, so it is not reported to Motzu.
 */

import { isBenignSyncSkipReason } from '../../pages/storeHome/deriveStoreSyncHealth';
import { skipReasonAction, type SkipReasonAction } from './skipReasonActions';

export type SkipGroup = 'unchanged' | 'notSent';

export type SkipKind =
  | 'unchanged'
  | 'proOnly'
  | 'refurbished'
  | 'rental'
  | 'syncDisabled'
  | 'noPrice'
  | 'belowMap'
  | 'suspicious'
  | 'confirmListing'
  | 'listingMismatch'
  | 'storePrice'
  | 'storePromo'
  | 'priceDrop'
  | 'priceJump'
  | 'belowCost'
  | 'other';

export interface SkipPresentation {
  kind: SkipKind;
  /** `unchanged` is still in sync. `notSent` is what the home page counts as blocked. */
  group: SkipGroup;
  /** The Sync Behavior control behind this row, when the store can change it. */
  settings: SkipReasonAction | null;
  /** Shopify listings waiting for the store to confirm an existing product. */
  reviewListings: boolean;
  /** The stored key was cut off, and it did not match a known sentence. */
  truncated: boolean;
}

const CONFIRM_LISTING =
  /shares a barcode|looks like|confirm the listing|may already be on Shopify|already uses this product handle|do not all agree|before a new one is created/i;

export function presentSkipReason(reason: string, platform?: string): SkipPresentation {
  const text = String(reason || '').trim();
  const settings = skipReasonAction(text, platform);
  const truncated = text.endsWith('…') || text.endsWith('...');

  const presented = (kind: SkipKind, group: SkipGroup, reviewListings = false): SkipPresentation => ({
    kind,
    group,
    settings,
    reviewListings,
    truncated: false,
  });

  if (isBenignSyncSkipReason(text)) return presented('unchanged', 'unchanged');
  if (/pro-only product/i.test(text)) return presented('proOnly', 'notSent');
  if (/refurbished product excluded/i.test(text)) return presented('refurbished', 'notSent');
  if (/rental\W*(loaner )?product excluded/i.test(text)) return presented('rental', 'notSent');
  if (/product sync is disabled/i.test(text)) return presented('syncDisabled', 'notSent');
  if (/^No valid price\b/i.test(text)) return presented('noPrice', 'notSent');
  if (/below MAP/i.test(text)) return presented('belowMap', 'notSent');
  if (/suspiciously low price/i.test(text)) return presented('suspicious', 'notSent');
  if (/does not match this product|\bIdentity (?:no_match|review)\b|^Price not written to Shopify listing\b/i.test(text)) {
    return presented('listingMismatch', 'notSent', platform === 'shopify');
  }
  // Compare-at before a store price edit. Both refusals mention Resume Motzu pricing.
  if (/promotion stays/i.test(text) || (/price not written/i.test(text) && /compare-at/i.test(text))) {
    return presented('storePromo', 'notSent');
  }
  if (/left as you set it/i.test(text) || (/price not written/i.test(text) && /changed in Shopify/i.test(text))) {
    return presented('storePrice', 'notSent');
  }
  if (/less than half/i.test(text)) return presented('priceDrop', 'notSent');
  if (/much higher than the current|more than 50%/i.test(text)) return presented('priceJump', 'notSent');
  if (/below the cost|cost per item|below this listing/i.test(text)) return presented('belowCost', 'notSent');
  if (CONFIRM_LISTING.test(text) || (truncated && /\bConfirm\b/.test(text))) {
    return presented('confirmListing', 'notSent', platform === 'shopify');
  }

  return {
    kind: 'other',
    group: 'notSent',
    settings,
    reviewListings: false,
    truncated,
  };
}

export type FailureKind = 'rateLimit' | 'unavailable' | 'credentials' | 'other';

export interface FailurePresentation {
  kind: FailureKind;
  /** A product issue Motzu should see. A platform timeout or a bad connection is not one. */
  reportable: boolean;
}

export interface GroupedSkip {
  presentation: SkipPresentation;
  count: number;
  /** Stored notes for an unrecognized reason. Known reasons collapse to one row. */
  notes: string[];
}

/** One row per store-facing kind. Unrecognized notes stay separate so their text is still visible. */
export function groupSkipReasons(reasons: Record<string, number>, platform?: string): GroupedSkip[] {
  const merged = new Map<string, GroupedSkip>();
  for (const [reason, rawCount] of Object.entries(reasons)) {
    const count = Math.max(0, Math.floor(Number(rawCount) || 0));
    if (count === 0) continue;
    const presentation = presentSkipReason(reason, platform);
    const key = presentation.kind === 'other' ? `other:${reason}` : presentation.kind;
    const existing = merged.get(key);
    if (existing) {
      existing.count += count;
      if (presentation.kind === 'other' && !existing.notes.includes(reason)) existing.notes.push(reason);
      continue;
    }
    merged.set(key, {
      presentation,
      count,
      notes: presentation.kind === 'other' ? [reason] : [],
    });
  }
  const rank = (group: SkipGroup) => (group === 'notSent' ? 0 : 1);
  return Array.from(merged.values()).sort((a, b) => rank(a.presentation.group) - rank(b.presentation.group) || b.count - a.count);
}

/**
 * An HTTP status only counts when the error names it as one. A SKU, a handle or a price can contain
 * the same digits. BigCommerce writes `(Status: 429)`; axios writes `status code 429`; some gateways
 * write `HTTP 503`.
 */
function mentionsStatusCode(text: string, code: string): boolean {
  return new RegExp(`(?:status(?: code)?:?|HTTP) ${code}\\b`, 'i').test(text);
}

export function presentSyncFailure(raw: string): FailurePresentation {
  const text = String(raw || '');
  if (mentionsStatusCode(text, '429') || /rate.?limit|throttl|too many requests/i.test(text)) {
    return { kind: 'rateLimit', reportable: false };
  }
  if (
    mentionsStatusCode(text, '401') ||
    mentionsStatusCode(text, '403') ||
    /unauthori[sz]ed|forbidden|invalid.*(token|credential|api key)|access token/i.test(text)
  ) {
    return { kind: 'credentials', reportable: false };
  }
  if (
    mentionsStatusCode(text, '500') ||
    mentionsStatusCode(text, '502') ||
    mentionsStatusCode(text, '503') ||
    mentionsStatusCode(text, '504') ||
    /\bHTTP 5\d\d\b|\b5\d\d Internal Server Error\b|timed? ?out|timeout|ECONNRESET|ETIMEDOUT|service unavailable|bad gateway/i.test(text)
  ) {
    return { kind: 'unavailable', reportable: false };
  }
  return { kind: 'other', reportable: true };
}

/**
 * Keep a group's counts inside `cap`. Duplicate chunk work can leave skip-reason
 * totals above the reconciled `productsSkipped` the home page uses.
 * Excess comes off the largest rows so the heading still equals the rows.
 */
export function clampSkipCounts(rows: GroupedSkip[], cap: number): GroupedSkip[] {
  const limit = Math.max(0, Math.floor(cap));
  const total = rows.reduce((sum, row) => sum + row.count, 0);
  if (total <= limit) return rows;
  let excess = total - limit;
  const cuts = new Map<GroupedSkip, number>();
  for (const row of [...rows].sort((a, b) => b.count - a.count)) {
    if (excess <= 0) break;
    const cut = Math.min(row.count, excess);
    cuts.set(row, cut);
    excess -= cut;
  }
  return rows
    .map((row) => {
      const cut = cuts.get(row) || 0;
      return cut === 0 ? row : { ...row, count: row.count - cut };
    })
    .filter((row) => row.count > 0);
}
